import React from 'react'
import { Button, Card, Flex, Popconfirm, Switch, Typography } from 'antd'
import { DisconnectOutlined } from '@ant-design/icons'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { DISCONNECT_SITE, UPDATE_SITE } from '../../user/permission'
import { useDisconnectSite, useSite, useUpdateSite } from '../hooks'

export type SiteSettingsProps = {
    siteId: string
}

export function SiteSettings({ siteId }: SiteSettingsProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const navigate = useNavigate()
    const { data: site } = useSite(siteId)
    const disconnect = useDisconnectSite()
    const updateSite = useUpdateSite(siteId)
    return (
        <Flex
            vertical
            gap="large"
        >
            {can(UPDATE_SITE) && site?.type === 'wordpress' && (
                <Card title={t('WordPress')}>
                    <Flex
                        justify="space-between"
                        align="center"
                        gap="middle"
                    >
                        <Flex vertical>
                            <Typography.Text strong>{t('Show candidates in WordPress')}</Typography.Text>
                            <Typography.Text type="secondary">
                                {t(
                                    "Administrators and editors of the website see the candidates who applied via WhatsApp in the Evaa plugin in WordPress, read-only. Visitors of the website don't see them. Web agencies with access to the website see them too."
                                )}
                            </Typography.Text>
                        </Flex>
                        <Switch
                            checked={site.candidatesInWordpress ?? true}
                            loading={updateSite.isPending}
                            onChange={(candidatesInWordpress) => updateSite.mutate({ candidatesInWordpress })}
                        />
                    </Flex>
                </Card>
            )}

            {can(DISCONNECT_SITE) && site?.connected && (
                <Card
                    title={t('Disconnect')}
                    className="border-red-200"
                >
                    <Flex
                        justify="space-between"
                        align="center"
                        gap="middle"
                        wrap
                    >
                        <Typography.Text type="secondary">
                            {site.type === 'snippet'
                                ? t(
                                      'The chat disappears from the website, also when the code is still there, and Evaa stops reading its pages. Chats and candidates stay in Evaa.'
                                  )
                                : t(
                                      'The chat disappears from the website and Evaa stops reading its pages. Chats and candidates stay in Evaa.'
                                  )}
                        </Typography.Text>
                        <Popconfirm
                            title={t('Disconnect this website?')}
                            okText={t('Yes, disconnect')}
                            cancelText={t('Cancel')}
                            okButtonProps={{ danger: true }}
                            onConfirm={() =>
                                disconnect.mutate(siteId, { onSuccess: () => navigate({ to: '/websites' }) })
                            }
                        >
                            <Button
                                danger
                                icon={<DisconnectOutlined />}
                                loading={disconnect.isPending}
                            >
                                {t('Disconnect')}
                            </Button>
                        </Popconfirm>
                    </Flex>
                </Card>
            )}
        </Flex>
    )
}
